/**
 * Accounting routes.
 *
 * Mounted inside the platform's single shell. Paths are the ones the
 * accounting engine's own links, dashboard cards and printed documents
 * already point at, so none of those needed rewriting.
 */

import { lazy, Suspense } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { Spinner, EmptyState } from '@/components/shared/states';
import { useSession } from '@/stores/session';
import { RequirePermission } from '@/components/shared/RequirePermission';


const Masters = () => import('./pages/Masters');
const CreateCompany = lazy(() => import('@/modules/dashboard/CreateCompany').then((m) => ({ default: m.CreateCompany })));
const ProductMasterPage = lazy(() => import('./pages/ProductMaster').then((m) => ({ default: m.ProductMasterPage })));
const ProductCategoriesPage = lazy(() => import('@/modules/field/areas/catalog/ProductPages').then((m) => ({ default: m.ProductCategoriesPage })));
const PartiesPage = lazy(() => Masters().then((m) => ({ default: m.PartiesPage })));
const GenericMasterPage = lazy(() => Masters().then((m) => ({ default: m.GenericMasterPage })));
const PartyDetailPage = lazy(() => import('./pages/PartyDetail').then((m) => ({ default: m.PartyDetailPage })));
const ProductDetailPage = lazy(() => import('./pages/ProductDetail').then((m) => ({ default: m.ProductDetailPage })));



const Utilities = () => import('./pages/Utilities');
const ImportPage = lazy(() => Utilities().then((m) => ({ default: m.ImportPage })));
const BackupPage = lazy(() => Utilities().then((m) => ({ default: m.BackupPage })));
const AdvancedUtilitiesPage = lazy(() => Utilities().then((m) => ({ default: m.AdvancedUtilitiesPage })));
const DataHealthPage = lazy(() => Utilities().then((m) => ({ default: m.DataHealthPage })));
const DiaryPage = lazy(() => Utilities().then((m) => ({ default: m.DiaryPage })));
const AuditTrailPage = lazy(() => Utilities().then((m) => ({ default: m.AuditTrailPage })));

const Setup = () => import('./pages/Setup');
const CompanySetupPage = lazy(() => Setup().then((m) => ({ default: m.CompanySetupPage })));
const FinancialYearsPage = lazy(() => Setup().then((m) => ({ default: m.FinancialYearsPage })));
const SoftwareSettingsPage = lazy(() => Setup().then((m) => ({ default: m.SoftwareSettingsPage })));
const UsersPage = lazy(() => Setup().then((m) => ({ default: m.UsersPage })));
const RolesPage = lazy(() => Setup().then((m) => ({ default: m.RolesPage })));
const ApprovalRulesPage = lazy(() => Setup().then((m) => ({ default: m.ApprovalRulesPage })));



/** Every figure in accounting belongs to a company; without one there is nothing to show. */
function CompanyScoped({ children }: { children: React.ReactNode }) {
  const company = useSession((s) => s.company);
  const companies = useSession((s) => s.companies);
  const superadmin = useSession((s) => !!s.user?.is_superadmin);
  // A new installation: the administrator creates the company right here.
  if (!company && !companies.length && superadmin) {
    return <Suspense fallback={<Spinner label="Loading…" />}><CreateCompany /></Suspense>;
  }
  if (!company) {
    return (
      <EmptyState
        title="No company is selected"
        message={companies.length
          ? 'Choose a company from the switcher at the top of the screen.'
          : 'You do not have access to any company yet. Ask an administrator to add you to one.'}
      />
    );
  }
  return <Suspense fallback={<Spinner label="Loading…" />}>{children}</Suspense>;
}

const page = (element: React.ReactNode) => <CompanyScoped>{element}</CompanyScoped>;

/** Route elements, spread into the platform router inside the shell. */
export function accountingRoutes() {
  return [
    // Masters
    <Route key="m-parties" path="masters/parties" element={page(<PartiesPage />)} />,
    <Route key="m-party" path="masters/parties/:id" element={page(<PartyDetailPage />)} />,
    <Route key="m-products" path="masters/products" element={page(<ProductMasterPage />)} />,
    <Route key="m-product" path="masters/products/:id" element={page(<ProductDetailPage />)} />,
    // Division > primary > secondary, with images: the one category editor.
    <Route key="m-categories" path="masters/categories" element={page(<RequirePermission module="master.product" what="product categories"><ProductCategoriesPage /></RequirePermission>)} />,
    <Route key="m-generic" path="masters/:entity" element={page(<GenericMasterPage />)} />,

    // Transactions
    // Documents that are raised in Operations, which posts these same
    // vouchers: one door each. A static path outranks :type, so an old link
    // or bookmark lands on the screen that now owns the document.
    // Search and the dashboard link to vouchers under the accounting namespace.

    // Banking, TDS, registers

    // Orders and production

    // GST

    /*
     * Stock reports.
     *
     * These three screens asked /api/accounting/reports/stock/… , which has
     * answered 404 since the accounting routes were removed. They did not
     * fail loudly; they rendered, asked, got nothing, and printed "Nothing to
     * show for this view" — which a person reads as an empty godown rather
     * than a broken screen. Two of them were in the menu and one was the
     * Stock row in the sidebar, so the most common question in a
     * distribution business, "what have we got", was answered with silence.
     *
     * The warehouse screen has served exactly this from /api/erp/warehouse
     * the whole time, and better: batch, age, expiry and value, not just a
     * quantity. So these addresses now go there rather than nowhere, which
     * keeps every bookmark and in-app link that still points at them working.
     */
    <Route key="r-stock" path="reports/stock" element={<Navigate to="/erp/warehouse" replace />} />,
    <Route key="r-stock-l" path="reports/stock/ledger/:productId" element={<Navigate to="/erp/warehouse/movements" replace />} />,
    <Route key="r-stock-v" path="reports/stock/:view" element={<Navigate to="/erp/warehouse" replace />} />,

    // Utilities
    <Route key="u-import" path="utilities/import" element={page(<ImportPage />)} />,
    <Route key="u-backup" path="utilities/backup" element={page(<BackupPage />)} />,
    <Route key="u-adv" path="utilities/advanced" element={page(<AdvancedUtilitiesPage />)} />,
    <Route key="u-health" path="utilities/health" element={page(<DataHealthPage />)} />,
    <Route key="u-diary" path="utilities/diary" element={page(<DiaryPage />)} />,
    <Route key="u-audit" path="utilities/audit" element={page(<AuditTrailPage />)} />,

    // Setup and administration
    <Route key="s-co" path="setup/company" element={page(<CompanySetupPage />)} />,
    <Route key="s-fy" path="setup/financial-years" element={page(<FinancialYearsPage />)} />,
    <Route key="s-set" path="setup/settings" element={page(<SoftwareSettingsPage />)} />,
    <Route key="s-users" path="setup/users" element={page(<UsersPage />)} />,
    <Route key="s-roles" path="setup/roles" element={page(<RolesPage />)} />,
    <Route key="s-appr" path="setup/approvals" element={page(<ApprovalRulesPage />)} />,
  ];
}
